import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
  catchError,
  combineLatest,
  merge,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';

import { Resource } from '@shared/resource/models';
import { Location } from '@shared/location/models';
import { Schedule } from '@shared/schedule/models';
import { AuthUser } from '@shared/signup/models';
import { Service } from '@shared/service/models';
import { Party } from '@shared/party/models';
import { DateTimeService, FloorplanResources } from '@tablesready/common-ui';
import { FloorPlanPartyType, PartyActionEvent, FloorPlanPartyTab } from '@tablesready/frontend-timeline';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { DayGridViewComponent, FLOOR_PLAN_VIEW_TABLET_BREAKPOINT, FloorplanViewComponent } from './components';
import { FloorPlanItemService } from '../services/floor-plan-item.service';
import { EditResourcesComponent } from '../waitlist/components';
import {
  MarkPartyService,
  ReservationService,
  ResourcesService,
  ScheduleService,
  ServicesService,
  WaitlistService,
  FloorPlanTableService,
  PlanRestrictionsAlertService,
  PricePlansRestrictionsService,
} from '../services';
import { PlansAlertData } from '../models';
import { NewTimelinePartyData } from './models';

enum ReservationPageTab {
  floorplan = 'floorplan',
  grid = 'grid',
}

@Component({
  selector: 'tablesready-floorplan',
  templateUrl: './floorplan.component.html',
  styleUrls: ['./floorplan.component.scss'],
  providers: [FloorPlanTableService],
})
export class FloorplanComponent implements OnInit, OnDestroy {
  @ViewChild(FloorplanViewComponent) floorPlanView?: FloorplanViewComponent;
  @ViewChild(DayGridViewComponent) dayGridView?: DayGridViewComponent;
  resources: Resource[] = [];
  schedule: Schedule[] = [];
  services: Service[] = [];
  location?: Location;
  user: AuthUser;
  minDate: NgbDateStruct;
  dateFilter: NgbDateStruct;
  isLocationOnBusinessPlan = false;
  tablesCount = 0;
  selectedFloorPlanId?: string;
  currentTab = '';
  floorplanResources: FloorplanResources[] = [];
  pageTabs = ReservationPageTab;
  currentPageTab = ReservationPageTab.floorplan;
  floorPlanEnabled = false;
  servicesAndResourcesEnabled = false;
  floorPlanPartyTab = FloorPlanPartyTab;
  trialCompleted?: boolean;
  plansAlertData?: PlansAlertData;

  icon = AppIcon;
  size = AppIconSize;
  color = AppIconColor;

  private readonly subscriptions = new Subscription();

  constructor(
    readonly service: ReservationService,
    private readonly itemService: FloorPlanItemService,
    private readonly modalController: FrontendModalService,
    private readonly waitlistService: WaitlistService,
    private readonly alertsService: AlertsService,
    private readonly scheduleService: ScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly resourcesService: ResourcesService,
    private readonly servicesService: ServicesService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly headerTitleService: HeaderTitleService,
    private readonly dateTimeService: DateTimeService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly floorPlanTableService: FloorPlanTableService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly planRestrictionsAlertService: PlanRestrictionsAlertService,
  ) { }

  private floorSub = new Subscription();

  ngOnDestroy(): void {
    this.waitlistService.clearModalSubs();
  }

  ionViewWillEnter(): void {
    const dataSub = combineLatest([
      this.authRestaurantService.selectWithAccountAndLocation(),
      this.servicesService.selectAllEnabled(),
      this.resourcesService.selectAllEnabled(),
      this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape]),
    ]).pipe(
      tap(([user, services, resources, { matches: isMobileScreen }]) => {
        this.floorPlanEnabled = user.account?.features?.floorPlan && !isMobileScreen;
        this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);
        this.location = user.location;
        this.user = user;
        this.trialCompleted = user.account.recurly?.trial;
        this.minDate = this.dateTimeService.momentToNgbDate(moment.tz(this.location.timezone).subtract(14, 'days'));
        this.headerTitleService.setDataForRoute('floorplan', { title: this.floorPlanEnabled ? 'Floor Plan' : 'Timeline' });
        this.resources = resources;
        this.services = services;
        this.servicesAndResourcesEnabled = this.location.resourceSettings.enabled && this.resources.length > 0 && this.location.serviceSettings.enabled && this.services.length > 0;
        this.plansAlertData = this.planRestrictionsAlertService.getAlertData(
          user.account.subscriptionType,
          user.account.recurly,
          this.location.recurly,
        );
      }),
    ).subscribe();
    this.floorSub.add(dataSub);
  }

  ionViewDidEnter(): void {
    this.floorPlanView?.pageLoad();

    this.currentPageTab = ReservationPageTab.floorplan;
    this.floorPlanView?.clearSearchBar();
    this.floorPlanView?.setCurrentDate();
    this.floorPlanView?.tabChanged(0);

    const floorPlanItemService = this.itemService.listenToChanges()
      .subscribe((changes) => {
        this.floorPlanView?.floorPlanSwitcherChange(this.selectedFloorPlanId);
      });
    this.floorSub.add(floorPlanItemService);
  }

  ionViewWillLeave(): void {
    this.floorSub.unsubscribe();
    this.dayGridView?.setDefaultSortValue();

    const isTabletLayout = this.breakpointObserver.isMatched(FLOOR_PLAN_VIEW_TABLET_BREAKPOINT);
    if (isTabletLayout) {
      this.floorPlanView?.setSidebarOpen(false);
    }
  }

  ngOnInit(): void {
    this.headerTitleService.setDataForRoute('floorplan', { title: 'Floor Plan' });

    const scheduleSub = this.scheduleService.selectAll()
      .pipe(
        tap((schedule) => {
          this.schedule = schedule;
        }),
        catchError(() => {
          this.alertsService.addError('Please, configure business hours', 'ERROR:');

          return of(null);
        }),
      )
      .subscribe();
    this.subscriptions.add(scheduleSub);

    const dataChangesSub = this.listenToChanges().subscribe();
    this.subscriptions.add(dataChangesSub);
  }

  getResourceName = (resource: Resource): string => {
    let name = `${resource.name} (${resource.capacity.min}-${resource.capacity.max})`;

    if (this.floorPlanEnabled) {
      name += ` ${this.floorPlanTableService.getFloorPlanNameByResourceId(resource._id)}`;
    }

    return name;
  };

  editParty(action: PartyActionEvent): void {
    if (action.cardType === FloorPlanPartyType.waiting) {
      this.addEditWaitlist(action);

      return;
    }

    this.addEditReservation(action);
  }

  addParty(
    floorPlanTabActive: boolean,
    addWalkIn: boolean,
    serveNow: boolean,
    extraAction?: Partial<PartyActionEvent>,
  ): void {
    if (addWalkIn && floorPlanTabActive) {
      let action: PartyActionEvent = {
        cardType: FloorPlanPartyType.waiting,
        party: undefined,
        serveNow,
      };

      if (extraAction !== undefined) {
        action = {
          ...action,
          ...extraAction,
        };
      }

      if (serveNow) {
        action.callback = (createdParty: Party): void => {
          this.checkForServiceTracking(
            {
              ...action,
              party: createdParty,
            },
            ({ party }: PartyActionEvent) => {
              this.markPartySeated(party.name, {
                party_id: party._id,
                seated_time: Date.now(),
              });
            },
          );
        };
      }

      this.addEditWaitlist(action);
      return;
    }

    let action = {
      cardType: FloorPlanPartyType.booked,
      party: undefined,
    };

    if (extraAction !== undefined) {
      action = {
        ...action,
        ...extraAction,
      };
    }

    this.addEditReservation(action);
  }

  addEditWaitlist({ party, callback, resourceId, serveNow, skipName, showSuccessUpdateAlert }: PartyActionEvent): void {
    const selectedResource = this.resources.find(res => res._id === resourceId);
    this.waitlistService.addEditParty(
      this.user,
      [],
      this.schedule,
      selectedResource,
      true,
      this.floorplanResources,
      false,
      resourceId,
      party,
      serveNow,
      showSuccessUpdateAlert,
      skipName,
    ).subscribe((updatedParty) => {
      if (!updatedParty) {
        return;
      }

      this.handleCallback(updatedParty, callback);
    });
  }

  addReservationViaTimeline({ time, endTime, resourceId }: NewTimelinePartyData): void {
    const selectedResource: Resource = this.resources.find(res => res._id === resourceId);
    this.service.addEditReservation(
      this.user,
      selectedResource,
      time,
      endTime,
      this.dateFilter,
      false,
      this.floorplanResources,
      false,
      true,
      undefined,
      undefined,
    )
      .pipe(take(1))
      .subscribe();
  }

  addEditReservation({ party, callback, resourceId, showSuccessUpdateAlert }: PartyActionEvent): void {
    const selectedResource: Resource = this.resources.find(res => res._id === resourceId);
    this.service.addEditReservation(
      this.user,
      selectedResource,
      undefined,
      undefined,
      this.dateFilter,
      this.floorPlanEnabled,
      this.floorplanResources,
      false,
      showSuccessUpdateAlert,
      resourceId,
      party,
    )
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.handleCallback(result, callback);
      });
  }

  confirmDelete({ party, cardType, callback }: PartyActionEvent): void {
    this.service.confirmDelete(party)
      .pipe(take(1))
      .subscribe(() => {
        this.handleCallback(undefined, callback);
      });
  }

  checkIn({ party, callback }: PartyActionEvent): void {
    this.service.checkIn(party, this.location)
      .pipe(take(1))
      .subscribe((updatedParty) => {
        this.handleCallback(updatedParty, callback);
      });
  }

  checkInAndServe(action: PartyActionEvent): void {
    const { skipCheckServeTracking } = action;

    if (skipCheckServeTracking) {
      const { party, callback } = action;
      this.service.checkInAndServe(party, this.location)
        .pipe(take(1))
        .subscribe((updatedParty) => {
          this.handleCallback(updatedParty, callback);
        });

      return;
    }

    this.checkForServiceTracking(action, ({ party, callback }: PartyActionEvent) => {
      this.service.checkInAndServe(party, this.location)
        .pipe(take(1))
        .subscribe((updatedParty) => {
          this.handleCallback(updatedParty, callback);
        });
    });
  }

  complete({ party, callback }: PartyActionEvent): void {
    this.service.completeParty(party, this.location, false)
      .pipe(take(1))
      .subscribe(() => {
        this.handleCallback(callback);
      });
  }

  serve(action: PartyActionEvent): void {
    const { skipCheckServeTracking, serveNow } = action;

    const options = {
      party_id: action.party._id,
      seated_time: new Date().getTime(),
    };

    if (skipCheckServeTracking && !serveNow) {
      this.markPartySeated(action.party.name, options, action.callback);

      return;
    }

    this.checkForServiceTracking(action, ({ party, callback }: PartyActionEvent) => {
      this.markPartySeated(party.name, options, callback);
    });
  }

  partyChat({ party, callback }: PartyActionEvent): void {
    this.service.chatParty(party, this.user);
    this.handleCallback(undefined, callback);
  }

  switchPageTab(tab: ReservationPageTab): void {
    this.currentPageTab = tab;
  }

  get isNotOnBusinessPlan(): boolean {
    return this.currentPageTab === ReservationPageTab.floorplan && !this.isLocationOnBusinessPlan;
  }

  private checkForServiceTracking(action: PartyActionEvent, callback: (ev: PartyActionEvent) => void): void {
    if (!action.party?.resources?.length && (this.location?.bookingOptions?.servingTracking || action.serveNow)) {
      this.modalController.create<EditResourcesComponent, Party>(EditResourcesComponent, {
        location: this.user.location,
        party: action.party,
        schedule: this.schedule,
        title: 'Assign Services & Resources',
      })
        .pipe(
          take(1),
          tap((newPartyData) => {
            callback({
              ...action,
              party: newPartyData,
            });
          }),
          catchError((err) => {
            return of(err);
          }),
        )
        .subscribe();
    } else {
      callback(action);
    }
  }

  private markPartySeated(
    partyName: string,
    options: { party_id: string; seated_time: number },
    callback?: (createdParty: Party) => void,
  ): void {
    this.markPartyService.setParty(options)
      .pipe(
        tap(() => this.alertsService.addSuccess(`${partyName} served.`)),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe(() => {
        this.handleCallback(undefined, callback);
      });
  }

  private handleCallback<T>(updatedParty?: T, callback?: (party?: T) => void): void {
    if (!callback) {
      return;
    }

    callback(updatedParty);
  }

  private listenToChanges() {
    return merge(this.resourcesService.listenToChanges(), this.servicesService.listenToChanges());
  }
}
